// src/utils/gstBill.js
// D-291 (Stabilization P3a) - pure GST maths for one vendor purchase bill.
// P3b adds the master-data helpers (vendor GSTIN reader, ITC rule, HSN check).
//
// WHY THIS FILE EXISTS
// A vendor's bill must be entered in Zeno exactly the way the vendor printed
// it: per line  qty x rate before GST = taxable;  GST worked out on the
// taxable TOTAL of each GST rate (not line by line);  same state -> CGST +
// SGST, other state -> IGST;  then a round off to the rupee. If Zeno rounds
// differently from the vendor, the khaata shows a few paise "due" forever.
// Before P3 the GRN screen took one GST-inclusive rate and a silent 18 %, so
// nobody could check a bill against what Zeno stored.
//
// Everything here is pure (no Firebase). The bill screen calls computeBill()
// on every keystroke to show the totals; purchaseBillService (P3d) calls it
// again inside its transaction and refuses to save when ok is false.
// CGST / SGST / IGST are ALWAYS computed here - never typed by a user.
//
// Money is worked in whole paise (integers), so float noise such as
// 0.1 + 0.2 = 0.30000000000000004 can never change a total.
// Rounding is half-up (1.005 -> 1.01), like vendors' billing software.
//
// Pure functions -> covered by src/utils/gstBill.test.js (Jest).

export const SAME_STATE = "intra"; // CGST + SGST
export const OTHER_STATE = "inter"; // IGST

// Rates seen on Indian bills. A rate outside this list is allowed (the law
// changes) but gets a warning, so a typo like 1.8 instead of 18 is noticed.
export const COMMON_GST_RATES = [0, 0.1, 0.25, 1.5, 3, 5, 12, 18, 28, 40];
export const MAX_GST_PERCENT = 40;
export const MAX_ROUND_OFF = 1; // rupees, either way
export const PRINTED_TOTAL_TOLERANCE = 1; // rupees - more than this = warning

// ---------------------------------------------------------------- numbers

// "" / null / undefined -> NaN, so an empty box is never read as 0.
const num = (v) => (v === "" || v === null || v === undefined ? NaN : Number(v));

// Round to `places` decimals, half-up, with no float error: going through the
// decimal string ("1.005e2" = 100.5 exactly) instead of 1.005 * 100 = 100.4999...
export const roundHalfUp = (value, places = 2) => {
  const n = num(value);
  if (!Number.isFinite(n)) return NaN;
  const abs = Math.abs(n);
  const s = String(abs);
  const scaled = s.includes("e") ? abs * 10 ** places : Number(`${s}e${places}`);
  const r = Math.round(scaled);
  if (r === 0) return 0; // never -0
  return ((n < 0 ? -1 : 1) * r) / 10 ** places;
};

// true when n has no more than `places` decimals (241.100 -> 3 is fine).
const hasAtMostDecimals = (n, places) => roundHalfUp(n, places) === n;

export const toPaise = (rupees) => Math.round(roundHalfUp(rupees, 2) * 100);
export const fromPaise = (paise) => (paise === 0 ? 0 : paise / 100);

// ---------------------------------------------------------------- GSTIN

const GSTIN_CHARS = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ";
// 2-digit state code + PAN (5 letters, 4 digits, 1 letter) + entity no. + "Z" + check character
const GSTIN_PATTERN = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;

export const normaliseGstin = (text) => String(text ?? "").replace(/\s+/g, "").toUpperCase();

// The 15th character of a GSTIN is a check character over the first 14
// (mod-36 Luhn). A wrong one means a character was mistyped.
export function gstinCheckChar(first14) {
  let sum = 0;
  for (let i = 0; i < 14; i++) {
    const v = GSTIN_CHARS.indexOf(first14[i]);
    const p = v * (i % 2 === 1 ? 2 : 1);
    sum += Math.floor(p / 36) + (p % 36);
  }
  return GSTIN_CHARS[(36 - (sum % 36)) % 36];
}

const validStateCode = (code) => {
  const n = Number(code);
  return (n >= 1 && n <= 38) || n === 97 || n === 99;
};

/**
 * @returns {{ ok: boolean, gstin: string, state_code: string|null, reason: string }}
 *   gstin = cleaned (no spaces, upper case) - save this, not the typed text.
 */
export function validateGstin(text) {
  const gstin = normaliseGstin(text);
  const fail = (reason) => ({ ok: false, gstin, state_code: null, reason });
  if (!gstin) return fail("GSTIN is empty");
  if (gstin.length !== 15) return fail(`GSTIN must be 15 characters (this one has ${gstin.length})`);
  if (!GSTIN_PATTERN.test(gstin)) return fail("GSTIN format is not valid (2-digit state code + PAN + 3 characters)");
  const state = gstin.slice(0, 2);
  if (!validStateCode(state)) return fail(`State code ${state} is not a valid GST state code`);
  if (gstinCheckChar(gstin.slice(0, 14)) !== gstin[14]) {
    return fail("GSTIN check character does not match - one character is mistyped");
  }
  return { ok: true, gstin, state_code: state, reason: "" };
}

/**
 * Same state (first 2 digits equal) -> "intra" (CGST + SGST), else "inter" (IGST).
 * null when either GSTIN is missing or invalid -> the bill screen asks once
 * "same state or other state".
 */
export function supplyTypeOf(companyGstin, vendorGstin) {
  const a = validateGstin(companyGstin);
  const b = validateGstin(vendorGstin);
  if (!a.ok || !b.ok) return null;
  return a.state_code === b.state_code ? SAME_STATE : OTHER_STATE;
}

// ---------------------------------------------------------------- masters (P3b)

// GST rates offered in Item Master's GST % box ("" = not set -> the PO line
// must have GST typed; never a silent 18).
// GST 2.0 slabs only (D-291, Ravi 20 Oct): since 22 Sep 2025 the main
// slabs for goods are 5 / 18 / 40 %, plus 0 for exempt items. Bills and PO
// lines still accept any rate 0-40 (an unusual rate only warns).
export const ITEM_GST_OPTIONS = [0, 5, 18, 40];

// A vendor's GSTIN. Vendor Master saves `gst_number`; Quick Create used to
// save `gst` (before P3b) - read both, so old vendors keep their GSTIN.
// Every screen that needs a vendor's GSTIN reads it through this function.
export const vendorGstinOf = (vendor) => normaliseGstin(vendor?.gst_number || vendor?.gst || "");

/**
 * "registered"   - a valid GSTIN is on the vendor
 * "unregistered" - the vendor was ticked "Unregistered vendor" (gst_registered: false)
 * "invalid"      - something is typed but it is not a valid GSTIN (old data)
 * "not_entered"  - nothing yet -> the bill screen asks "same state / other state" once
 */
export function vendorGstStatus(vendor) {
  if (vendor?.gst_registered === false) return "unregistered";
  const gstin = vendorGstinOf(vendor);
  if (!gstin) return "not_entered";
  return validateGstin(gstin).ok ? "registered" : "invalid";
}

// ITC (GST input credit) can only be claimed with a valid company GSTIN.
// Default Yes for a registered company (claims_itc missing = Yes).
export const companyClaimsItc = (company) =>
  validateGstin(company?.gst_number).ok && company?.claims_itc !== false;

// D-291 P3d-4c: GST input credit on ONE purchase needs BOTH sides - the
// company claims ITC AND the vendor has a valid GSTIN (GST law: no credit
// on a bill from a supplier without a valid GSTIN). This is the one rule
// for the GRN / Direct Site rate pre-fill, the bill's stock cost and the
// bill's ITC amount. Unregistered / invalid / not-entered vendor -> false.
export const itcOnPurchase = (company, vendor) =>
  companyClaimsItc(company) && vendorGstStatus(vendor) === "registered";

/**
 * HSN / SAC code check for Item Master and bill lines. Optional:
 * blank -> ok with "". Otherwise 4, 6 or 8 digits (spaces removed).
 * @returns {{ ok: boolean, hsn: string, reason: string }}
 */
export function checkHsn(text) {
  const hsn = String(text ?? "").replace(/\s+/g, "");
  if (!hsn) return { ok: true, hsn: "", reason: "" };
  if (!/^\d+$/.test(hsn)) return { ok: false, hsn, reason: "HSN / SAC code can have digits only" };
  if (![4, 6, 8].includes(hsn.length)) return { ok: false, hsn, reason: "HSN / SAC code must be 4, 6 or 8 digits" };
  return { ok: true, hsn, reason: "" };
}

// ---------------------------------------------------------------- rates

// Vendors often print "rate incl. of tax". Typing it gives the rate before GST
// (2 decimals) - the bill is always calculated from the rate before GST.
export function baseFromInclusive(rateInclusive, gstPercent) {
  const incl = num(rateInclusive);
  const gst = num(gstPercent);
  if (!(incl >= 0) || !(gst >= 0)) return NaN;
  return roundHalfUp((incl * 100) / (100 + gst), 2);
}

export function inclusiveFromBase(baseRate, gstPercent) {
  const base = num(baseRate);
  const gst = num(gstPercent);
  if (!(base >= 0) || !(gst >= 0)) return NaN;
  return roundHalfUp((base * (100 + gst)) / 100, 2);
}

// ---------------------------------------------------------------- the bill

/**
 * @param {Object} bill
 * @param {Array}  bill.lines        [{ line_id, billed_qty, base_rate, gst_percent }]
 *                                   billed_qty: up to 3 decimals (241.100 kg)
 *                                   base_rate:  rate before GST, up to 2 decimals
 * @param {string} bill.supplyType   "intra" | "inter"
 * @param {number} [bill.roundOff]   leave empty -> to the nearest rupee;
 *                                   typed -> must be within -1.00 .. +1.00
 *                                   (e.g. 0 for a vendor who does not round)
 * @param {number} [bill.printedTotal] "Total as printed on the bill" - only checked
 * @returns {Object} { ok, errors[], warnings[], supply_type, lines[{line_id,
 *   gst_percent, taxable}], by_rate[{gst_percent, taxable, cgst, sgst, igst}],
 *   taxable_total, cgst, sgst, igst, tax_total, round_off, bill_total,
 *   printed_total, printed_diff }  - all money in rupees (2 decimals).
 *   Lines with an error get taxable null and are left out of the totals.
 *   Without a valid supplyType the tax, round off and bill total are null.
 */
export function computeBill({ lines, supplyType, roundOff, printedTotal } = {}) {
  const errors = [];
  const warnings = [];
  const list = Array.isArray(lines) ? lines : [];
  if (list.length === 0) errors.push("Add at least one line");

  const supplyOk = supplyType === SAME_STATE || supplyType === OTHER_STATE;
  if (!supplyOk) errors.push("Choose same state (CGST + SGST) or other state (IGST)");

  const outLines = [];
  const groups = new Map(); // GST rate in basis points (18 % = 1800) -> taxable paise

  list.forEach((line, i) => {
    const label = `Line ${i + 1}`;
    const qty = num(line?.billed_qty);
    const rate = num(line?.base_rate);
    const gst = num(line?.gst_percent);
    const before = errors.length;

    if (!(qty > 0)) errors.push(`${label}: quantity must be more than 0`);
    else if (!hasAtMostDecimals(qty, 3)) errors.push(`${label}: quantity can have at most 3 decimals`);

    if (!(rate >= 0)) errors.push(`${label}: rate before GST is missing`);
    else if (!hasAtMostDecimals(rate, 2)) errors.push(`${label}: rate before GST can have at most 2 decimals`);

    if (!(gst >= 0 && gst <= MAX_GST_PERCENT)) errors.push(`${label}: GST % must be between 0 and ${MAX_GST_PERCENT}`);
    else if (!hasAtMostDecimals(gst, 2)) errors.push(`${label}: GST % can have at most 2 decimals`);

    const lineId = line?.line_id ?? null;
    if (errors.length > before) {
      outLines.push({ line_id: lineId, gst_percent: Number.isFinite(gst) ? gst : null, taxable: null });
      return;
    }
    if (!COMMON_GST_RATES.includes(gst)) warnings.push(`${label}: GST ${gst} % is unusual - check the bill`);

    const qtyMilli = Math.round(qty * 1000);
    const ratePaise = toPaise(rate);
    const product = qtyMilli * ratePaise;
    if (!Number.isSafeInteger(product)) {
      errors.push(`${label}: amount is too large`);
      outLines.push({ line_id: lineId, gst_percent: gst, taxable: null });
      return;
    }
    const taxablePaise = Math.round(product / 1000);
    outLines.push({ line_id: lineId, gst_percent: gst, taxable: fromPaise(taxablePaise) });

    const bp = Math.round(gst * 100);
    groups.set(bp, (groups.get(bp) || 0) + taxablePaise);
  });

  // GST per rate, on that rate's taxable total (how billing software does it).
  let taxablePaiseTotal = 0;
  let cgstP = 0;
  let sgstP = 0;
  let igstP = 0;
  const byRate = [...groups.keys()]
    .sort((a, b) => a - b)
    .map((bp) => {
      const t = groups.get(bp);
      taxablePaiseTotal += t;
      let c = 0;
      let s = 0;
      let g = 0;
      if (supplyType === SAME_STATE) {
        c = Math.round((t * bp) / 20000); // half the rate each
        s = c;
      } else if (supplyType === OTHER_STATE) {
        g = Math.round((t * bp) / 10000);
      }
      cgstP += c;
      sgstP += s;
      igstP += g;
      return {
        gst_percent: bp / 100,
        taxable: fromPaise(t),
        cgst: supplyOk ? fromPaise(c) : null,
        sgst: supplyOk ? fromPaise(s) : null,
        igst: supplyOk ? fromPaise(g) : null,
      };
    });

  const taxP = cgstP + sgstP + igstP;
  const beforeRoundP = taxablePaiseTotal + taxP;

  // Round off: empty -> to the nearest rupee (half-up); typed -> within +-1.00.
  let roundOffP = 0;
  const manualRoundOff = roundOff !== undefined && roundOff !== null && roundOff !== "";
  if (manualRoundOff) {
    const r = num(roundOff);
    if (!Number.isFinite(r) || Math.abs(r) > MAX_ROUND_OFF || !hasAtMostDecimals(r, 2)) {
      errors.push(`Round off must be between -${MAX_ROUND_OFF}.00 and +${MAX_ROUND_OFF}.00`);
    } else {
      roundOffP = toPaise(r);
    }
  } else {
    roundOffP = Math.round(beforeRoundP / 100) * 100 - beforeRoundP;
  }
  const billTotalP = beforeRoundP + roundOffP;

  // "Total as printed on the bill" - never changes anything, only warns.
  let printed = null;
  let printedDiff = null;
  const hasPrinted = printedTotal !== undefined && printedTotal !== null && printedTotal !== "";
  if (hasPrinted) {
    const p = num(printedTotal);
    if (!Number.isFinite(p) || p < 0) {
      errors.push("Total as printed is not a valid amount");
    } else {
      printed = roundHalfUp(p, 2);
      if (supplyOk) {
        const diffP = toPaise(p) - billTotalP;
        printedDiff = fromPaise(diffP);
        if (Math.abs(diffP) > PRINTED_TOTAL_TOLERANCE * 100) {
          warnings.push(
            `Bill total ${fromPaise(billTotalP).toFixed(2)} differs from the printed total ${printed.toFixed(2)} ` +
              `by ${(Math.abs(diffP) / 100).toFixed(2)} - check the lines`
          );
        }
      }
    }
  }

  return {
    ok: errors.length === 0,
    errors,
    warnings,
    supply_type: supplyOk ? supplyType : null,
    lines: outLines,
    by_rate: byRate,
    taxable_total: fromPaise(taxablePaiseTotal),
    cgst: supplyOk ? fromPaise(cgstP) : null,
    sgst: supplyOk ? fromPaise(sgstP) : null,
    igst: supplyOk ? fromPaise(igstP) : null,
    tax_total: supplyOk ? fromPaise(taxP) : null,
    round_off: supplyOk ? fromPaise(roundOffP) : null,
    bill_total: supplyOk ? fromPaise(billTotalP) : null,
    printed_total: printed,
    printed_diff: printedDiff,
  };
}
// ---------------------------------------------------------------- the PO value (P3c)

/**
 * The ORDERED value of a purchase order - what the approver approves and the
 * vendor sees on the PO (D-291 P3c). Per line:
 *   ordered qty x rate before GST - discount = taxable (worked in paise),
 * then GST per rate on that rate's taxable total - done by computeBill()
 * itself (each line is handed over as qty 1 x its taxable), so a PO and the
 * vendor's bill can never round GST differently. No round off on a PO.
 *
 * @param {Array} lines  PO items[] as stored, or the screen's lines:
 *   { line_id, item_name, ordered_qty (or qty), base_rate (old lines: rate),
 *     discount_type "percent" | "flat", discount_value, gst_percent,
 *     rate_not_agreed, _cancelled }
 * @param {string|null} [supplyType] "intra" | "inter" | null. null = the
 *   CGST / SGST / IGST split is not known yet (a GSTIN missing) - the GST
 *   total is then worked as one rate (it can differ from the bill's
 *   CGST + SGST by a paisa per rate; the bill decides).
 * @returns {Object} { ok, errors[], warnings[], supply_type, lines[{line_id,
 *   status, gst_percent, taxable}], by_rate[{gst_percent, taxable, cgst,
 *   sgst, igst, tax}], taxable_total, cgst, sgst, igst, tax_total, total,
 *   rate_not_agreed[line_id], rate_missing[line_id] } - money in rupees.
 *   status: "ok" | "rate_not_agreed" (ticked on purpose) | "rate_missing"
 *   (old line saved without a rate) | "error". Only "ok" lines are in the
 *   totals; cancelled lines are left out completely. An empty box is never
 *   read as 0: a line without GST % is an error, not 0 %.
 */
export function computeOrderValue(lines, supplyType = null) {
  const errors = [];
  const warnings = [];
  const outLines = [];
  const notAgreed = [];
  const missing = [];
  const billLines = [];
  const splitKnown = supplyType === SAME_STATE || supplyType === OTHER_STATE;

  (Array.isArray(lines) ? lines : []).forEach((line, i) => {
    if (!line || line._cancelled) return;
    const label = String(line.item_name || "").trim() || `Line ${i + 1}`;
    const lineId = line.line_id ?? null;
    const gstRaw = num(line.gst_percent);
    const gstShown = Number.isFinite(gstRaw) ? gstRaw : null;

    if (line.rate_not_agreed === true) {
      notAgreed.push(lineId);
      outLines.push({ line_id: lineId, status: "rate_not_agreed", gst_percent: gstShown, taxable: null });
      return;
    }

    const qtyRaw = line.ordered_qty !== undefined && line.ordered_qty !== "" ? line.ordered_qty : line.qty;
    const qty = num(qtyRaw);
    let rate = num(line.base_rate);
    if (!Number.isFinite(rate)) rate = num(line.rate);

    const lineErrors = [];
    if (!(qty > 0)) lineErrors.push("quantity must be more than 0");
    else if (!hasAtMostDecimals(qty, 3)) lineErrors.push("quantity can have at most 3 decimals");

    if (lineErrors.length === 0 && !(rate > 0)) {
      missing.push(lineId);
      outLines.push({ line_id: lineId, status: "rate_missing", gst_percent: gstShown, taxable: null });
      return;
    }
    if (rate > 0 && !hasAtMostDecimals(rate, 2)) lineErrors.push("rate before GST can have at most 2 decimals");

    if (!Number.isFinite(gstRaw)) lineErrors.push("GST % is missing");
    else if (gstRaw < 0 || gstRaw > MAX_GST_PERCENT) lineErrors.push(`GST % must be between 0 and ${MAX_GST_PERCENT}`);
    else if (!hasAtMostDecimals(gstRaw, 2)) lineErrors.push("GST % can have at most 2 decimals");

    const discountType = line.discount_type === "flat" ? "flat" : "percent";
    const dRaw = line.discount_value;
    const discount = dRaw === "" || dRaw === null || dRaw === undefined ? 0 : Number(dRaw);
    if (!Number.isFinite(discount) || discount < 0) lineErrors.push("discount cannot be negative");
    else if (!hasAtMostDecimals(discount, 2)) lineErrors.push("discount can have at most 2 decimals");
    else if (discountType === "percent" && discount > 100) lineErrors.push("discount % cannot be more than 100");

    let taxablePaise = null;
    if (lineErrors.length === 0) {
      const product = Math.round(qty * 1000) * toPaise(rate);
      if (!Number.isSafeInteger(product)) {
        lineErrors.push("amount is too large");
      } else {
        const subtotalPaise = Math.round(product / 1000);
        const discountPaise = discountType === "flat"
          ? toPaise(discount)
          : Math.round((subtotalPaise * Math.round(discount * 100)) / 10000);
        if (discountPaise > subtotalPaise) lineErrors.push("discount is more than the line amount");
        else taxablePaise = subtotalPaise - discountPaise;
      }
    }

    if (lineErrors.length > 0) {
      lineErrors.forEach((e) => errors.push(`${label}: ${e}`));
      outLines.push({ line_id: lineId, status: "error", gst_percent: gstShown, taxable: null });
      return;
    }
    if (!COMMON_GST_RATES.includes(gstRaw)) warnings.push(`${label}: GST ${gstRaw} % is unusual - check it`);
    outLines.push({ line_id: lineId, status: "ok", gst_percent: gstRaw, taxable: fromPaise(taxablePaise) });
    billLines.push({ line_id: lineId, billed_qty: 1, base_rate: fromPaise(taxablePaise), gst_percent: gstRaw });
  });

  let taxableTotal = 0;
  let cgst = splitKnown ? 0 : null;
  let sgst = splitKnown ? 0 : null;
  let igst = splitKnown ? 0 : null;
  let taxTotal = 0;
  let byRate = [];
  if (billLines.length > 0) {
    // Split unknown -> worked as one rate (same arithmetic as IGST).
    const bill = computeBill({ lines: billLines, supplyType: splitKnown ? supplyType : OTHER_STATE, roundOff: 0 });
    bill.errors.forEach((e) => errors.push(e)); // never expected: every line was checked above
    taxableTotal = bill.taxable_total;
    taxTotal = bill.tax_total ?? 0;
    if (splitKnown) {
      cgst = bill.cgst;
      sgst = bill.sgst;
      igst = bill.igst;
    }
    byRate = bill.by_rate.map((r) => ({
      gst_percent: r.gst_percent,
      taxable: r.taxable,
      cgst: splitKnown ? r.cgst : null,
      sgst: splitKnown ? r.sgst : null,
      igst: splitKnown ? r.igst : null,
      tax: fromPaise(toPaise(r.cgst || 0) + toPaise(r.sgst || 0) + toPaise(r.igst || 0)),
    }));
  }

  return {
    ok: errors.length === 0,
    errors,
    warnings,
    supply_type: splitKnown ? supplyType : null,
    lines: outLines,
    by_rate: byRate,
    taxable_total: taxableTotal,
    cgst,
    sgst,
    igst,
    tax_total: taxTotal,
    total: fromPaise(toPaise(taxableTotal) + toPaise(taxTotal)),
    rate_not_agreed: notAgreed,
    rate_missing: missing,
  };
}